/**
 * The three numbers beside the header's `Programy`.
 *
 * ── The header describes the LIBRARY; the list describes the QUERY ──────────────────────────
 * The canon's own no-results specimen still reads `8 · 6 · 2` with a phrase active, which is why
 * this counts every plan and never the filtered view. A header that counted the filtered rows would
 * say `0 PROGRAMÓW` to a trainer who has eight.
 *
 * ── The ORDER is the BACKEND's, and this module no longer has one ───────────────────────────
 * Owner decision D1, 3 September 2026: `Programy v2 - D3` drops the sort control, and the rows are
 * shown in the order the read produced. That is not "no order": `PlanTemplateRepository`
 * `GetLibraryByOwnerAsync` applies `.OrderByDescending(pt => pt.UpdatedAt ?? pt.CreatedAt)` —
 * verified in the C#, line 73 — so the library arrives freshest first, which is the axis the
 * dropped control offered and the default direction it opened on.
 *
 * An earlier revision of this header and of the slice's `AGENTS.md` both claimed the endpoint
 * applies no ordering at all. **That was wrong and is withdrawn.** It is why `sortPlanRows` existed
 * and why it re-sorted a list that was already sorted.
 *
 * The one thing that comparator did which the backend does not is break EXACT ties by name and id.
 * Two plans stamped to the same millisecond can come back in either order between refetches; that
 * is recorded in the slice's `AGENTS.md` rather than answered here, because answering it would mean
 * re-sorting the whole list to settle a case the seeded data has never produced.
 *
 * **The filter moved out on wave 3** to `model/planLibraryFilter.ts`, which is where it belongs
 * once it stopped being a search box and two categories: four facets, two scopes and two row shapes
 * are not "the view", and the counts here are read by a part of the screen the facets never reach.
 *
 * Pure. No React, no DOM, no i18n.
 */

import type { PlanLibraryRow } from './planLibraryRow';

/** The three numbers beside the header's `Programy`. */
export interface PlanLibrarySummary {
  readonly total: number;
  /** Plans at least one client is currently following. */
  readonly assigned: number;
  /** The rest. Stated as its own number rather than left to subtraction on the screen. */
  readonly idle: number;
}

/**
 * Count the library.
 *
 * Over EVERY plan, never the filtered view — KANON's no-results specimen still reads `12 · 7 · 5`
 * with two categories and a phrase active, because the header describes the trainer's library and
 * the table describes their query. A header that counted the filtered rows would say `0 PROGRAMÓW`
 * to a trainer who has twelve.
 */
export function summarisePlanLibrary(rows: readonly PlanLibraryRow[]): PlanLibrarySummary {
  const assigned = rows.filter((row) => row.assignedClientCount > 0).length;
  return { total: rows.length, assigned, idle: rows.length - assigned };
}
